import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  notificationDashboardKPIs,
  notificationEventTypes,
  eventTypeMetrics,
  radioNotifConfigs,
  programNotifConfigs,
  hostNotifConfigs,
  clubNotifConfigs,
  planNotifLimits,
  getTotalEnabledEvents,
  formatMetricValue,
} from "@/mocks/rfwSmartNotificationsHub";

type ConfigTab = "radio" | "programa" | "locutor" | "club";

const configTabs: { key: ConfigTab; label: string; icon: string; color: string; count: number }[] = [
  { key: "radio", label: "Radios", icon: "ri-radio-line", color: "text-rose-500", count: radioNotifConfigs.length },
  { key: "programa", label: "Programas", icon: "ri-mic-line", color: "text-emerald-500", count: programNotifConfigs.length },
  { key: "locutor", label: "Locutores", icon: "ri-user-voice-line", color: "text-amber-500", count: hostNotifConfigs.length },
  { key: "club", label: "Clubes", icon: "ri-group-line", color: "text-violet-500", count: clubNotifConfigs.length },
];

export default function NotificacionesInteligentesSection() {
  const navigate = useNavigate();
  const [configTab, setConfigTab] = useState<ConfigTab>("radio");

  const allEnabledRadios = getTotalEnabledEvents(radioNotifConfigs);
  const allEnabledPrograms = getTotalEnabledEvents(programNotifConfigs);
  const allEnabledHosts = getTotalEnabledEvents(hostNotifConfigs);
  const allEnabledClubs = getTotalEnabledEvents(clubNotifConfigs);
  const globalEnabled = allEnabledRadios + allEnabledPrograms + allEnabledHosts + allEnabledClubs;

  return (
    <section className="py-12 md:py-16 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* ===== HEADER ===== */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-bold mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-500"></span>
            </span>
            Notificaciones Inteligentes
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Traé a tus oyentes de regreso
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
            Notificaciones que entienden qué le importa a cada oyente. Configurá por radio, programa, locutor o club. 
            Cada notificación es una razón para volver.
          </p>
        </div>

        {/* ===== KPI STRIP ===== */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-10">
          {notificationDashboardKPIs.map((kpi) => (
            <div
              key={kpi.id}
              className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5 hover:border-background-300 transition-colors"
            >
              <div className="flex items-center gap-2.5 mb-2">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${kpi.color}`}>
                  <i className={`${kpi.icon} text-base`}></i>
                </div>
                <span className={`text-xs font-bold ${
                  kpi.trend === "up" ? "text-emerald-600" : kpi.trend === "down" ? "text-red-500" : "text-foreground-400"
                }`}>
                  {kpi.trend === "up" ? "▲" : kpi.trend === "down" ? "▼" : "▬"} {kpi.trendPercent}%
                </span>
              </div>
              <p className="text-2xl md:text-3xl font-extrabold text-foreground-950 mb-0.5">{kpi.displayValue}</p>
              <p className="text-xs text-foreground-400">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* ===== TWO COLUMN LAYOUT ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* LEFT: Event triggers + Config */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Triggers Grid */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-foreground-700 uppercase tracking-wider">
                  Eventos que Disparan Notificaciones
                </h3>
                <span className="text-xs text-foreground-400 bg-background-100 px-2.5 py-1 rounded-full font-medium">
                  8 tipos de evento
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                {notificationEventTypes.map((evt) => {
                  const metrics = eventTypeMetrics.find((m) => m.eventId === evt.id);
                  return (
                    <div
                      key={evt.id}
                      className="bg-background-50 border border-background-200 rounded-2xl p-3.5 hover:border-background-300 transition-all group cursor-default"
                    >
                      <div className="flex items-center gap-2 mb-2.5">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${evt.bgColor}`}>
                          <i className={`${evt.icon} ${evt.color} text-sm`}></i>
                        </div>
                        <span className="text-xs font-bold text-foreground-900 leading-tight">{evt.label}</span>
                      </div>
                      {metrics && (
                        <div className="grid grid-cols-2 gap-1.5">
                          <div className="bg-background-100 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-[10px] text-foreground-400 leading-none mb-0.5">Enviadas</p>
                            <p className="text-xs font-bold text-foreground-800">{formatMetricValue(metrics.sent)}</p>
                          </div>
                          <div className="bg-background-100 rounded-lg px-2 py-1.5 text-center">
                            <p className="text-[10px] text-foreground-400 leading-none mb-0.5">CTR</p>
                            <p className="text-xs font-bold text-emerald-600">{metrics.ctr}%</p>
                          </div>
                        </div>
                      )}
                      <p className="text-[10px] text-foreground-400 mt-2 leading-tight hidden md:block">{evt.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Config Tabs + Detail */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-foreground-700 uppercase tracking-wider">
                  Configuración Personalizada
                </h3>
                <span className="text-xs text-foreground-400 bg-background-100 px-2.5 py-1 rounded-full font-medium">
                  {globalEnabled} activos
                </span>
              </div>

              {/* Tab Switcher */}
              <div className="flex items-center gap-1 mb-3 bg-background-100 rounded-2xl p-1">
                {configTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setConfigTab(tab.key)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex-1 justify-center ${
                      configTab === tab.key
                        ? "bg-background-50 text-foreground-950 shadow-sm"
                        : "text-foreground-400 hover:text-foreground-600"
                    }`}
                  >
                    <i className={`${tab.icon} ${configTab === tab.key ? tab.color : ""} text-sm`}></i>
                    {tab.label}
                    <span className="text-[10px] opacity-60">({tab.count})</span>
                  </button>
                ))}
              </div>

              {/* Config Cards */}
              <div className="space-y-2">
                {/* Radio Configs */}
                {configTab === "radio" && radioNotifConfigs.map((radio) => (
                  <div key={radio.radioId} className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex items-center gap-3 hover:border-background-300 transition-all">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={radio.radioImage} alt={radio.radioName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-900">{radio.radioName}</p>
                      <p className="text-[10px] text-foreground-400">{radio.radioFrequency}</p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {radio.events.filter((e) => e.enabled).slice(0, 4).map((e) => {
                        const evt = notificationEventTypes.find((t) => t.id === e.eventId);
                        return evt ? (
                          <span key={e.eventId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${evt.bgColor} ${evt.color}`}>
                            <i className={`${evt.icon} text-[9px]`}></i>
                            {evt.label}
                          </span>
                        ) : null;
                      })}
                      {radio.events.filter((e) => e.enabled).length > 4 && (
                        <span className="text-[10px] text-foreground-400 font-medium px-1">+{radio.events.filter((e) => e.enabled).length - 4}</span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Program Configs */}
                {configTab === "programa" && programNotifConfigs.map((prog) => (
                  <div key={prog.programId} className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex items-center gap-3 hover:border-background-300 transition-all">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={prog.programImage} alt={prog.programName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-900">{prog.programName}</p>
                      <p className="text-[10px] text-foreground-400">{prog.radioName}</p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {prog.events.filter((e) => e.enabled).map((e) => {
                        const evt = notificationEventTypes.find((t) => t.id === e.eventId);
                        return evt ? (
                          <span key={e.eventId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${evt.bgColor} ${evt.color}`}>
                            <i className={`${evt.icon} text-[9px]`}></i>
                            {evt.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                ))}

                {/* Host Configs */}
                {configTab === "locutor" && hostNotifConfigs.map((host) => (
                  <div key={host.hostId} className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex items-center gap-3 hover:border-background-300 transition-all">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={host.hostImage} alt={host.hostName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-900">{host.hostName}</p>
                      <p className="text-[10px] text-foreground-400">{host.radioName}</p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {host.events.filter((e) => e.enabled).map((e) => {
                        const evt = notificationEventTypes.find((t) => t.id === e.eventId);
                        return evt ? (
                          <span key={e.eventId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${evt.bgColor} ${evt.color}`}>
                            <i className={`${evt.icon} text-[9px]`}></i>
                            {evt.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                ))}

                {/* Club Configs (NEW) */}
                {configTab === "club" && clubNotifConfigs.map((club) => (
                  <div key={club.clubId} className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex items-center gap-3 hover:border-background-300 transition-all">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
                      <img src={club.clubImage} alt={club.clubName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-900">{club.clubName}</p>
                      <p className="text-[10px] text-foreground-400">{club.radioName} · {formatMetricValue(club.members)} miembros</p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {club.events.filter((e) => e.enabled).map((e) => {
                        const evt = notificationEventTypes.find((t) => t.id === e.eventId);
                        return evt ? (
                          <span key={e.eventId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${evt.bgColor} ${evt.color}`}>
                            <i className={`${evt.icon} text-[9px]`}></i>
                            {evt.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Dashboard Metrics + Plan Limits */}
          <div className="space-y-5">
            {/* Per-Event Metrics Panel */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
              <h3 className="text-sm font-bold text-foreground-700 uppercase tracking-wider mb-3">
                Métricas por Tipo de Evento
              </h3>
              <div className="space-y-2">
                {eventTypeMetrics.map((m) => {
                  const evt = notificationEventTypes.find((e) => e.id === m.eventId);
                  if (!evt) return null;
                  return (
                    <div key={m.eventId} className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${evt.bgColor}`}>
                        <i className={`${evt.icon} ${evt.color} text-xs`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-end justify-between mb-0.5">
                          <span className="text-xs text-foreground-600 font-medium truncate">{evt.label}</span>
                          <span className="text-xs font-bold text-foreground-900">{m.ctr}% CTR</span>
                        </div>
                        {/* Mini bar */}
                        <div className="w-full h-1.5 bg-background-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-accent-500 transition-all"
                            style={{ width: `${m.ctr}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Plan Limits */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground-700 uppercase tracking-wider">
                  Límites por Plan
                </h3>
                <span className="text-[10px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">SA</span>
              </div>
              <div className="space-y-1.5">
                {planNotifLimits.map((plan) => (
                  <div
                    key={plan.planId}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-xl border transition-all ${
                      plan.popular
                        ? `${plan.bgColor} ${plan.borderColor}`
                        : `bg-background-50 ${plan.borderColor}`
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={`text-xs font-bold ${plan.color}`}>{plan.planName}</span>
                      {plan.popular && (
                        <span className="text-[9px] font-bold bg-primary-500 text-background-50 px-1.5 py-0.5 rounded-full">
                          POPULAR
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-foreground-800">{plan.notifLimitDisplay}</span>
                      <span className="text-[10px] text-foreground-400">{plan.price}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-foreground-400 mt-3 leading-tight">
                Los límites son controlados desde el Super Admin. Cada plan define cuántas notificaciones puede enviar una radio por mes.
              </p>
            </div>

            {/* CTA */}
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 md:p-5 text-center">
              <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center mx-auto mb-2.5">
                <i className="ri-notification-3-line text-background-50 text-lg"></i>
              </div>
              <p className="text-sm font-bold text-foreground-900 mb-1">¿Querés afinar tus notificaciones?</p>
              <p className="text-xs text-foreground-500 mb-3">
                Configurá qué querés recibir, de quién y cómo. Todo desde un solo lugar.
              </p>
              <button
                onClick={() => navigate("/notifications")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-background-50 font-bold text-sm cursor-pointer whitespace-nowrap transition-all"
              >
                <i className="ri-equalizer-line"></i>
                Configurar Ahora
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}